import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'react-feather';

/**
 * RangeCalendar — start→end date range picker.
 * Visual language mirrors the BackendUser MultiDatePicker (v3-mdp): gradient
 * selected cells, chevron nav, month/year jump selects. Unlike that widget it
 * selects a contiguous range and allows future dates (seasonal events upcoming).
 *
 * Click flow:
 *   • First click (no range, or range already complete) sets the start, clears end.
 *   • Second click sets the end. Clicking before the start swaps them.
 *
 * Props: { start, end, year, onChange }
 *   - start, end: ISO 'yyyy-mm-dd' strings ('' when unset)
 *   - year: number used to seed the initial visible month when no start yet
 *   - onChange(startIso, endIso): endIso is '' while only the start is chosen
 */

const isoOf = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const fromISO = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const RangeCalendar = ({ start = '', end = '', year, onChange }) => {
    const { t } = useTranslation();
    const today = useMemo(() => {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return t;
    }, []);

    const [cursor, setCursor] = useState(() => {
        if (start) return startOfMonth(fromISO(start));
        const y = Number(year) || today.getFullYear();
        return new Date(y, 0, 1);
    });

    // Build a 6-week grid (42 cells) starting on the Monday on/before the 1st.
    const cells = useMemo(() => {
        const firstOfMonth = startOfMonth(cursor);
        const leadingBlank = (firstOfMonth.getDay() + 6) % 7; // Mon=0..Sun=6
        const gridStart = new Date(firstOfMonth);
        gridStart.setDate(firstOfMonth.getDate() - leadingBlank);
        const out = [];
        for (let i = 0; i < 42; i += 1) {
            const d = new Date(gridStart);
            d.setDate(gridStart.getDate() + i);
            out.push(d);
        }
        return out;
    }, [cursor]);

    const inCurrentMonth = (d) => d.getMonth() === cursor.getMonth();

    const handleDayClick = useCallback(
        (d) => {
            const iso = isoOf(d);
            // No start yet, or a complete range exists → begin a fresh range.
            if (!start || (start && end)) {
                onChange(iso, '');
                return;
            }
            // Have a start, picking the end.
            if (iso < start) onChange(iso, start); // clicked before start → swap
            else onChange(start, iso);
        },
        [start, end, onChange],
    );

    const prevMonth = () => {
        const c = new Date(cursor);
        c.setMonth(c.getMonth() - 1);
        setCursor(c);
    };
    const nextMonth = () => {
        const c = new Date(cursor);
        c.setMonth(c.getMonth() + 1);
        setCursor(c);
    };

    // Year options span a generous window around today, always including the cursor year.
    const years = useMemo(() => {
        const minY = Math.min(today.getFullYear() - 5, cursor.getFullYear());
        const maxY = Math.max(today.getFullYear() + 10, cursor.getFullYear());
        const out = [];
        for (let y = minY; y <= maxY; y += 1) out.push(y);
        return out;
    }, [today, cursor]);

    return (
        <div className="v3-rc">
            <div className="v3-rc-head">
                <button type="button" className="v3-rc-nav" onClick={prevMonth} aria-label={t('Previous month')}>
                    <ChevronLeft size={16} />
                </button>
                <div className="v3-rc-jump">
                    <select
                        className="v3-rc-jump-select"
                        value={cursor.getMonth()}
                        onChange={(e) => setCursor(new Date(cursor.getFullYear(), Number(e.target.value), 1))}
                        aria-label={t('Month')}
                    >
                        {MONTH_NAMES.map((m, i) => (
                            <option key={m} value={i}>{t(m)}</option>
                        ))}
                    </select>
                    <select
                        className="v3-rc-jump-select"
                        value={cursor.getFullYear()}
                        onChange={(e) => setCursor(new Date(Number(e.target.value), cursor.getMonth(), 1))}
                        aria-label={t('Year')}
                    >
                        {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
                <button type="button" className="v3-rc-nav" onClick={nextMonth} aria-label={t('Next month')}>
                    <ChevronRight size={16} />
                </button>
            </div>

            <div className="v3-rc-weekdays">
                {WEEKDAYS.map((w) => (
                    <span key={w}>{t(w)}</span>
                ))}
            </div>

            <div className="v3-rc-grid" role="grid">
                {cells.map((d) => {
                    const iso = isoOf(d);
                    const out = !inCurrentMonth(d);
                    const isStart = start && iso === start;
                    const isEnd = end && iso === end;
                    const mid = start && end && iso > start && iso < end;
                    const isToday = sameDay(d, today);
                    return (
                        <button
                            key={iso}
                            type="button"
                            className={[
                                'v3-rc-cell',
                                out ? 'out' : '',
                                isStart ? 'start' : '',
                                isEnd ? 'end' : '',
                                mid ? 'mid' : '',
                                isToday ? 'today' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => handleDayClick(d)}
                            aria-pressed={!!(isStart || isEnd || mid)}
                            aria-label={iso}
                        >
                            {d.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default RangeCalendar;
